import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Utensils, ShoppingCart, DollarSign, Package } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TabletPOS = () => {
  const { selectedProperty } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [menuRes, ordersRes, tablesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/pos/menu?property_id=${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/pos/orders?property_id=${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/pos/tables?property_id=${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/pos/sales/summary?property_id=${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);

      setMenu(menuRes.data);
      setOrders(ordersRes.data);
      setTables(tablesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createMenuItem = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      await axios.post(`${API}/pos/menu/item`, null, {
        params: {
          property_id: selectedProperty.id,
          name: 'Sample Item',
          category: 'Main Course',
          price: 15.99,
          description: 'Delicious sample item'
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Menu item created!');
      fetchData();
    } catch (error) {
      toast.error('Failed to create menu item');
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="tablet-pos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tablet POS</h1>
          <p className="text-slate-600 mt-1">Restaurant & bar point of sale system</p>
        </div>
        <Button onClick={createMenuItem} className="gap-2">
          <Utensils size={16} />
          Add Menu Item
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{summary.total_orders}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Paid Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">{summary.paid_orders}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">${summary.total_revenue}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Avg Order</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">${summary.average_order_value}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Utensils size={20} />
                Menu Items ({menu.length})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {menu.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Utensils className="mx-auto mb-2" size={40} />
                <p>No menu items created</p>
                <Button onClick={createMenuItem} className="mt-4">
                  Create Sample Item
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {menu.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-xs text-slate-600">{item.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-blue-600">${item.price}</p>
                      {item.available ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Available</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Unavailable</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart size={20} />
              Recent Orders ({orders.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <ShoppingCart className="mx-auto mb-2" size={40} />
                <p>No orders yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {orders.slice(0, 10).map((order) => (
                  <div key={order.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {order.table_number || order.unit_id || 'Takeout'}
                        </span>
                        <Badge className={
                          order.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          order.status === 'preparing' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        }>
                          {order.status}
                        </Badge>
                      </div>
                      <span className="font-bold text-blue-600">${order.total}</span>
                    </div>
                    <p className="text-xs text-slate-600">
                      {order.items.length} items • {order.guest_name || 'Guest'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>POS Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                  <Utensils size={20} />
                </div>
                <h3 className="font-semibold">Menu Management</h3>
              </div>
              <p className="text-sm text-slate-600">
                Create and manage menu items with categories, pricing, and availability
              </p>
            </div>

            <div className="p-4 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                  <ShoppingCart size={20} />
                </div>
                <h3 className="font-semibold">Order Processing</h3>
              </div>
              <p className="text-sm text-slate-600">
                Take orders, calculate totals with tax, and track order status
              </p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white">
                  <Package size={20} />
                </div>
                <h3 className="font-semibold">Inventory Tracking</h3>
              </div>
              <p className="text-sm text-slate-600">
                Monitor stock levels with low-stock alerts and reorder management
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Table Management ({tables.length} tables)</CardTitle>
        </CardHeader>
        <CardContent>
          {tables.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No tables configured
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {tables.map((table) => (
                <div
                  key={table.id}
                  className={`p-4 rounded-lg text-center border-2 ${
                    table.status === 'available'
                      ? 'bg-emerald-50 border-emerald-200'
                      : table.status === 'occupied'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <p className="font-bold text-lg">{table.number}</p>
                  <p className="text-xs text-slate-600">{table.capacity} seats</p>
                  <Badge className="mt-1 text-xs">
                    {table.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TabletPOS;
